import { api } from './api';
import type { LocationPoint } from './types';

type PositionHandler = (point: LocationPoint) => void;

/**
 * Something went wrong with *this browser's own* position. Nothing else.
 *
 * The browser's geolocation is one participant in the family network, not the
 * network itself. When it fails, family members keep reporting, Find Now keeps
 * working, and native background tracking is entirely unaffected — so the raw
 * GeolocationPositionError.message ("Position update is unavailable", and
 * similar) must never be surfaced as though the family device network had
 * failed. The product-facing wording says what actually stopped working, and
 * the platform's own words are kept alongside as a technical detail.
 */
export type SelfLocationIssue = {
  /** Product-facing copy. Never a raw platform message. */
  message: string;
  /** The platform's own wording, for the technical readout only. */
  detail: string;
};

/** The browser could not produce a position for the person using it. */
export const SELF_LOCATION_UNAVAILABLE =
  'Your current position is temporarily unavailable. Family locations are still updating normally.';

/** A position was obtained but this browser could not report it. */
export const SELF_LOCATION_NOT_SHARED =
  'This browser could not share its position just now. Family locations are still updating normally.';

type IssueHandler = (issue: SelfLocationIssue) => void;
let watchId: number | null = null;
let lastSentAt = 0;
let lastLat: number | null = null;
let lastLng: number | null = null;

function toPoint(position: GeolocationPosition, source: string): LocationPoint {
  const c = position.coords;
  return { latitude: c.latitude, longitude: c.longitude, accuracy: c.accuracy, altitude: c.altitude, speed: c.speed,
    heading: c.heading, recordedAt: new Date(position.timestamp || Date.now()).toISOString(), source };
}
function metersBetween(aLat: number, aLng: number, bLat: number, bLng: number) {
  const rad = Math.PI / 180; const x = (bLng - aLng) * rad * Math.cos(((aLat + bLat) / 2) * rad); const y = (bLat - aLat) * rad;
  return Math.sqrt(x * x + y * y) * 6371000;
}
async function upload(deviceId: string, point: LocationPoint, force = false, requestId?: string) {
  const moved = lastLat == null || lastLng == null ? Infinity : metersBetween(lastLat, lastLng, point.latitude, point.longitude);
  const elapsed = Date.now() - lastSentAt;
  if (!force && moved < 10 && elapsed < 15000) return point;
  const uploaded = await api<LocationPoint>(`/devices/${deviceId}/location`, { method: 'POST', body: JSON.stringify({ ...point, requestId }) });
  lastSentAt = Date.now(); lastLat = point.latitude; lastLng = point.longitude;
  return uploaded;
}
export function canUseGeolocation() { return 'geolocation' in navigator; }

/**
 * Periodic foreground reacquisition.
 *
 * watchPosition only fires when the browser decides the position changed, so a
 * stationary page can go many minutes without a callback and its location ages
 * into STALE while visibly open. This tops it up on a timer instead.
 *
 * Deliberately cheap: high accuracy off and a cached fix up to a minute old is
 * acceptable, so no GPS spin-up. The server rejects the upload if the fix is not
 * good enough to improve on what it already holds.
 */
export async function refreshPeriodicLocation(deviceId: string) {
  if (!canUseGeolocation()) return;
  return new Promise<void>(resolve => {
    navigator.geolocation.getCurrentPosition(
      async position => {
        try { await upload(deviceId, toPoint(position, 'periodic'), true); } catch { /* presence is best-effort */ }
        resolve();
      },
      () => resolve(),
      { enableHighAccuracy: false, maximumAge: 60_000, timeout: 15_000 }
    );
  });
}

const FIND_NOW_TIMESTAMP_TOLERANCE_MS = 5_000;

function geolocationFailure(error: GeolocationPositionError) {
  if (error.code === error.PERMISSION_DENIED) return 'Location permission was denied.';
  if (error.code === error.POSITION_UNAVAILABLE) return 'The browser could not determine a location.';
  if (error.code === error.TIMEOUT) return 'Location acquisition timed out.';
  return error.message || 'Location acquisition failed.';
}

function acquireFreshPosition(options: PositionOptions, requestedAt: number) {
  return new Promise<GeolocationPosition>((resolve, reject) => {
    navigator.geolocation.getCurrentPosition(position => {
      // maximumAge: 0 asks the browser for a new observation, but implementations
      // may still hand back a cached point. A correlated Find Now may never be
      // completed from a point that predates this acquisition sequence.
      if (!Number.isFinite(position.timestamp) || position.timestamp < requestedAt - FIND_NOW_TIMESTAMP_TOLERANCE_MS) {
        reject(new Error('The browser returned an older cached location.'));
        return;
      }
      resolve(position);
    }, error => reject(new Error(geolocationFailure(error))), options);
  });
}

export async function locateNow(deviceId: string, source = 'web-locate-now', requestId?: string) {
  if (!canUseGeolocation()) throw new Error('This browser does not provide geolocation.');
  const requestedAt = Date.now();
  let position: GeolocationPosition;
  try {
    position = await acquireFreshPosition({ enableHighAccuracy: true, timeout: 17_000, maximumAge: 0 }, requestedAt);
  } catch (highAccuracyError) {
    try {
      // Some browsers cannot satisfy a GPS-grade request indoors even though a
      // fresh Wi-Fi/cell fix is available. This is a second acquisition, not a
      // movement callback or a previously uploaded point.
      position = await acquireFreshPosition({ enableHighAccuracy: false, timeout: 8_000, maximumAge: 0 }, requestedAt);
    } catch (lowerPowerError) {
      const high = highAccuracyError instanceof Error ? highAccuracyError.message : 'failed';
      const low = lowerPowerError instanceof Error ? lowerPowerError.message : 'failed';
      throw new Error(`Unable to get fresh location. High-accuracy attempt: ${high} Lower-power attempt: ${low}`);
    }
  }
  return upload(deviceId, toPoint(position, source), true, requestId);
}
export function startLocationWatch(deviceId: string, onPoint: PositionHandler, onIssue: IssueHandler) {
  if (!canUseGeolocation()) throw new Error('This browser does not provide geolocation.');
  if (watchId != null) navigator.geolocation.clearWatch(watchId);
  watchId = navigator.geolocation.watchPosition(async position => {
    const point = toPoint(position, 'web-watch'); onPoint(point);
    try {
      await upload(deviceId, point);
    } catch (error) {
      // The position exists; only this browser's own reporting of it failed.
      onIssue({ message: SELF_LOCATION_NOT_SHARED, detail: error instanceof Error ? error.message : 'Location upload failed' });
    }
  }, error => onIssue({ message: SELF_LOCATION_UNAVAILABLE, detail: geolocationFailure(error) }),
  { enableHighAccuracy: true, maximumAge: 5000, timeout: 20000 });
  return watchId;
}
export function stopLocationWatch() { if (watchId != null && 'geolocation' in navigator) navigator.geolocation.clearWatch(watchId); watchId = null; }
